// @flow
import update from 'immutability-helper';
import {ActionsObservable, ofType} from 'redux-observable';
import {forkJoin, of} from 'rxjs';
import {concatMap, map, switchMap} from 'rxjs/operators';
import {
  ActionCreator,
  buildCatchError,
  createReducer,
} from '../../../store/utils';
import {uploadImageStoreApi} from '../../file-storage/api';
import {
  saveGeneralContact,
  saveGeneralInformationApi,
  saveStoreProfileInformationApi,
  saveGeneralSettingsApi,
} from './api';
import {
  createMultiFieldFormData,
  createSingleFieldFormData,
} from '../../../utils/form';

import type {StoreInformation, Store, StoredFile} from '../../../entities';
import type {Action} from '../../../store/utils';
import type {
  SaveGeneralContactRequest,
  SaveGeneralInformationRequest,
  SaveStoreProfileInformationRequest,
  SaveGeneralSettingsRequest,
} from './api';
import analytics from '../../../analytics';
import {getBasicDomain} from '../../../utils/component';

export type GeneralConfigAction = Action & {
  error?: string,
  store?: Store,
  dataGeneral?: SaveGeneralSettingsRequest,
  dataInformation?: SaveGeneralInformationRequest,
  storeProfileInformation?: SaveStoreProfileInformationRequest,
  storeId?: string,
  imageFile?: File,
  information?: StoreInformation,
  dataContact?: SaveGeneralContactRequest[],
};

export type GeneralConfigState = {
  isSaving?: boolean,
  error?: string,
  savingSuccess?: boolean,
};

export class SaveGeneralSettings extends ActionCreator<GeneralConfigAction> {}
export class SaveGeneralSettingsSuccess extends ActionCreator<GeneralConfigAction> {}
export class SaveGeneralSettingsErrorFn extends ActionCreator<GeneralConfigAction> {}

export class SaveGeneralInformation extends ActionCreator<GeneralConfigAction> {}
export class SaveGeneralInformationSuccess extends ActionCreator<GeneralConfigAction> {}
export class SaveGeneralInformationErrorFn extends ActionCreator<GeneralConfigAction> {}

export class SaveStoreProfileInformation extends ActionCreator<GeneralConfigAction> {}
export class SaveStoreProfileInformationSuccess extends ActionCreator<GeneralConfigAction> {}
export class SaveStoreProfileInformationErrorFn extends ActionCreator<GeneralConfigAction> {}

export class SaveGeneralContact extends ActionCreator<GeneralConfigAction> {}
export class SaveGeneralContactSuccess extends ActionCreator<GeneralConfigAction> {}
export class SaveGeneralContactErrorFn extends ActionCreator<GeneralConfigAction> {}

export class GeneralResetState extends ActionCreator<GeneralConfigAction> {}

export const actions = {
  saveGeneralSettings: (payload: GeneralConfigAction) =>
    new SaveGeneralSettings(payload),
  saveGeneralInformation: (payload: GeneralConfigAction) =>
    new SaveGeneralInformation(payload),
  saveStoreProfileInformation: (payload: GeneralConfigAction) =>
    new SaveStoreProfileInformation(payload),
  saveGeneralContact: (payload: GeneralConfigAction) =>
    new SaveGeneralContact(payload),
  resetState: () => new GeneralResetState(),
};

export const initialState = {};

export default createReducer<GeneralConfigState>(initialState, {
  [SaveGeneralSettings.type]: _ => ({isSaving: true}),
  [SaveGeneralSettingsSuccess.type]: (_, action: GeneralConfigAction) => {
    if (action.store && action.token)
      window.location.replace(
        `https://${getBasicDomain(action.store.domains)}/admin?d_c_token=${
          action.token
        }`
      );
    return {savingSuccess: true};
  },
  [SaveGeneralSettingsErrorFn.type]: (_, action: GeneralConfigAction) => ({
    error: action.error,
  }),
  [SaveGeneralInformation.type]: () => ({isSaving: true}),
  [SaveGeneralInformationSuccess.type]: () => ({savingSuccess: true}),
  [SaveGeneralInformationErrorFn.type]: (_, action: GeneralConfigAction) => ({
    error: action.error,
  }),
  [SaveStoreProfileInformation.type]: () => ({isSaving: true}),
  [SaveStoreProfileInformationSuccess.type]: () => ({savingSuccess: true}),
  [SaveStoreProfileInformationErrorFn.type]: (
    _,
    action: GeneralConfigAction
  ) => ({
    error: action.error,
  }),
  [SaveGeneralContact.type]: _ => ({isSaving: true}),
  [SaveGeneralContactSuccess.type]: () => ({savingSuccess: true}),
  [SaveGeneralContactErrorFn.type]: (_, action: GeneralConfigAction) => ({
    error: action.error,
  }),
  [GeneralResetState.type]: _ => initialState,
});

export function saveGeneralSettingsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveGeneralSettings.type),
    switchMap(({dataGeneral, token}: GeneralConfigAction) =>
      saveGeneralSettingsApi(dataGeneral).pipe(
        map((store: Store) => new SaveGeneralSettingsSuccess({store, token})),
        buildCatchError(SaveGeneralSettingsErrorFn)
      )
    )
  );
}

export function saveGeneralInformationEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveGeneralInformation.type),
    switchMap(({dataInformation}: GeneralConfigAction) => {
      const images = [];
      if (dataInformation.logoImageFile)
        images.push(dataInformation.logoImageFile);
      if (dataInformation.coverImageFile)
        images.push(dataInformation.coverImageFile);
      if (images.length) {
        return uploadImageStoreApi(
          createMultiFieldFormData(images, undefined, {filePrefix: 'ui/store'})
        ).pipe(map((files: StoredFile[]) => [files, dataInformation]));
      } else return of([undefined, dataInformation]);
    }),
    concatMap(
      ([
        files: ?(StoredFile[]),
        dataInformation: SaveGeneralInformationRequest,
      ]) => {
        const request = update(dataInformation, {
          $unset: ['logoImageFile', 'coverImageFile'],
        });
        const r = {...request};

        if (files) {
          if (dataInformation.logoImageFile && dataInformation.coverImageFile) {
            r.logoId = files[0].id;
            r.coverId = files[1].id;
          } else if (dataInformation.logoImageFile) r.logoId = files[0].id;
          else r.coverId = files[0].id;
        }
        return saveGeneralInformationApi(r).pipe(
          map((information: StoreInformation) => {
            if (dataInformation.logoImageFile)
              analytics.fireCustomEvent('Store Logo Configured', undefined, [
                'user-guiding',
              ]);
            if (dataInformation.coverImageFile)
              analytics.fireCustomEvent('Store Cover Configured', undefined, [
                'user-guiding',
              ]);
            return new SaveGeneralInformationSuccess({information});
          }),
          buildCatchError(SaveGeneralInformationErrorFn)
        );
      }
    )
  );
}

export function saveStoreProfileInformationEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveStoreProfileInformation.type),
    switchMap(({storeProfileInformation}: GeneralConfigAction) => {
      if (storeProfileInformation.profileImageFile)
        return uploadImageStoreApi(
          createSingleFieldFormData(
            storeProfileInformation.profileImageFile,
            undefined,
            {
              filePrefix: 'ui/store',
            }
          )
        ).pipe(map((files: StoredFile[]) => [files, storeProfileInformation]));
      else return of([undefined, storeProfileInformation]);
    }),
    concatMap(
      ([
        files: ?(StoredFile[]),
        storeProfileInformation: SaveStoreProfileInformationRequest,
      ]) => {
        const request = update(storeProfileInformation, {
          $unset: ['profileImageFile'],
        });
        const r = {...request};

        if (files && files[0]) r.profilePhotoId = files[0].id;
        return saveStoreProfileInformationApi(r).pipe(
          map(_ => new SaveStoreProfileInformationSuccess()),
          buildCatchError(SaveStoreProfileInformationErrorFn)
        );
      }
    )
  );
}

export function saveGeneralContactEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveGeneralContact.type),
    switchMap(({dataContact}: GeneralConfigAction) => {
      const tasks = [];
      dataContact.forEach(dc => {
        const obs = saveGeneralContact({
          data: dc.data,
          contactTypeId: dc.contactTypeId,
          storeContactId: dc.storeContactId,
        }).pipe(
          map(_ => null),
          buildCatchError(SaveGeneralContactErrorFn, 'Error al editar contacto')
        );
        tasks.push(obs);
      });
      return forkJoin(tasks).pipe(
        map((dataError: SaveGeneralContactErrorFn[]) => {
          const e = dataError.find(x => !!x);
          if (e) return e;
          else return new SaveGeneralContactSuccess();
        })
      );
    })
  );
}
